import React, { useState } from 'react';
import { Alert, Box, Button, FormControl, FormLabel, Input, Link, Textarea } from '@mui/joy';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useProfileDraft } from '../organizations/ProfileDrafts';
import { StaffPicker } from '../../../components/admin/StaffPicker';
import { AdminOnboardingAPI } from '../../../lib/apiAdmin';
import { getApiErrorMessage } from '../../../lib/apiError';
import type { Lead } from '../../../types/admin';

export const AssignContactTask: React.FC<{ lead: Lead }> = ({ lead }) => {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [assignee, setAssignee] = useState<number | null>(lead.owner ?? null);
  const [dueAt, setDueAt] = useState('');
  const [success, setSuccess] = useState('');

  const taskMutation = useMutation({
    mutationFn: () => AdminOnboardingAPI.createTask({
      title: title.trim(),
      notes: notes.trim(),
      lead: lead.id,
      organization: lead.converted_organization || lead.matched_organization || null,
      assignee,
      due_at: dueAt ? new Date(dueAt).toISOString() : null,
    }),
    onSuccess: (task) => {
      setSuccess(`“${task.title}” assigned${task.assignee_name ? ` to ${task.assignee_name}` : ''}.`);
      setTitle('');
      setNotes('');
      setDueAt('');
      setAssignee(lead.owner ?? null);
      queryClient.invalidateQueries({ queryKey: ['admin', 'task-queue'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'customers'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'customer'] });
    },
  });

  useProfileDraft('crm-task', !!(title || notes || dueAt), taskMutation.isPending);

  return (
    <Box
      component="form"
      sx={{ display: 'grid', gap: 1.5 }}
      onSubmit={(event) => {
        event.preventDefault();
        if (!title.trim() || assignee === null || taskMutation.isPending) return;
        setSuccess('');
        taskMutation.mutate();
      }}
    >
      <FormControl required>
        <FormLabel>Task title</FormLabel>
        <Input
          size="sm"
          placeholder="Follow up about their trial"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          slotProps={{ input: { maxLength: 255 } }}
          disabled={taskMutation.isPending}
        />
      </FormControl>
      <FormControl required>
        <FormLabel>Assign to</FormLabel>
        <StaffPicker
          value={assignee}
          onChange={setAssignee}
          placeholder="Choose a staff member"
          disabled={taskMutation.isPending}
        />
      </FormControl>
      <FormControl>
        <FormLabel>Due date and time (optional)</FormLabel>
        <Input
          size="sm"
          type="datetime-local"
          value={dueAt}
          onChange={(event) => setDueAt(event.target.value)}
          disabled={taskMutation.isPending}
        />
      </FormControl>
      <FormControl>
        <FormLabel>Task details (optional)</FormLabel>
        <Textarea
          size="sm"
          minRows={2}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          disabled={taskMutation.isPending}
        />
      </FormControl>
      {taskMutation.isError && (
        <Alert color="danger" role="alert">
          {getApiErrorMessage(taskMutation.error, 'This task could not be assigned. Please try again.')}
        </Alert>
      )}
      {success && (
        <Alert color="success" role="status" sx={{ flexWrap: 'wrap' }}>
          {success}
          <Link href="/manage/accounts/tasks">View task queue</Link>
        </Alert>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          size="sm"
          type="submit"
          disabled={!title.trim() || assignee === null}
          loading={taskMutation.isPending}
        >
          Assign task
        </Button>
      </Box>
    </Box>
  );
};
